import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import 'animate.css';
import { supabase } from '../supabaseClient';

// ──────────────────────────────────────────────────────────
// Paleta y tipografía del sitio DS cyber:
//   h1 → font-family: Nikaia
//   h2/h3 → font-family: Escobar
//   Body  → font-family: Montserrat
//   Color principal → #061C5B
// NOTA: el CSS global tiene span { -webkit-text-fill-color: transparent }
//       por eso los textos de color en span se hacen con style inline o
//       usando elementos alternativos (p, strong, etc.)
// ──────────────────────────────────────────────────────────

// ── Estilos responsive globales ────────────────────────────
const RECURSOS_STYLES = `
  /* ── Formulario bienvenida: 2 col → 1 col en móvil ── */
  .rcs-welcome-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 48px;
    align-items: center;
  }
  @media (max-width: 768px) {
    .rcs-welcome-grid {
      grid-template-columns: 1fr !important;
      gap: 32px !important;
      padding: 28px !important;
    }
    .rcs-hide-mobile {
      display: none !important;
    }
  }

  /* ── Tarjetas de categoría ── */
  .rcs-categorias-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 32px;
  }
  @media (max-width: 480px) {
    .rcs-categorias-grid {
      grid-template-columns: 1fr !important;
      gap: 20px !important;
    }
  }

  /* ── Hero padding ── */
  @media (max-width: 640px) {
    .rcs-hero { padding: 48px 20px 56px !important; }
    .rcs-hero h1 { font-size: 2.2rem !important; }
    .rcs-hero p  { font-size: 0.9rem !important; }
  }

  /* ── Panel encabezado ── */
  @media (max-width: 640px) {
    .rcs-panel-header { padding: 22px 18px !important; gap: 14px !important; }
    .rcs-panel-header h2 { font-size: 1.4rem !important; }
    .rcs-panel-header p  { font-size: 0.8rem !important; }
    .rcs-panel-header button { width: 100%; justify-content: center; }
  }

  /* ── Barra de filtros ── */
  .rcs-filtros-container {
    margin-bottom: 24px;
  }
  .rcs-filtros-titulo {
    font-family: Montserrat;
    font-weight: 700;
    font-size: 0.8rem;
    color: #6b7280;
    margin: 0 0 10px 0;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  .rcs-filtros {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
  }
  @media (max-width: 640px) {
    .rcs-filtros {
      overflow-x: auto;
      flex-wrap: nowrap;
      padding-bottom: 10px;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
    }
    .rcs-filtros::-webkit-scrollbar { display: none; }
    .rcs-filtros button { flex-shrink: 0; white-space: nowrap; }
  }

  /* Ocultar filtro por completo en móviles por ahora */
  @media (max-width: 768px) {
    .rcs-filtros-container { display: none !important; }
  }

  /* ── Tabla: ocultar cabecera md en móvil ── */
  @media (max-width: 768px) {
    .rcs-tabla-header { display: none !important; }
  }

  /* ── Fila de curso: tarjeta en móvil ── */
  .rcs-curso-fila-desktop {
    display: grid;
    grid-template-columns: 1fr 130px 130px 130px;
    gap: 12px;
    align-items: center;
  }
  @media (max-width: 768px) {
    .rcs-curso-fila-desktop { display: none !important; }
    .rcs-curso-fila-mobile { 
      display: flex !important; 
      flex-direction: column; 
      gap: 12px; 
      padding: 18px !important; 
      border: 1px solid #e5e7eb; 
      border-radius: 12px; 
      background-color: #ffffff;
      box-shadow: 0 4px 6px rgba(0,0,0,0.02);
      margin-bottom: 12px;
    }
  }
  @media (min-width: 769px) {
    .rcs-curso-fila-mobile  { display: none !important; }
  }

  /* ── Contenido principal ── */
  @media (max-width: 640px) {
    .rcs-main-content { padding: 32px 16px 56px !important; }
  }

  /* ── Formulario inputs ── */
  @media (max-width: 480px) {
    .rcs-form-box { padding: 22px 18px !important; }
  }
`;

const NAV_COLOR = '#061C5B';

const CATEGORIAS = [
    {
        id: 'videos',
        emoji: '🎬',
        titulo: 'Videos',
        subtitulo: 'Contenido audiovisual',
        descripcion: 'Aprende ciberseguridad, gestión de riesgos e inteligencia artificial con nuestros videos educativos producidos por expertos DS cyber.',
        accentColor: '#1d4ed8',   // azul
        borderColor: '#3b82f6',
        items: [
            // { titulo: 'Intro a Ciberseguridad', url: '#', duracion: '12:30' },
        ],
        placeholder: 'Los videos estarán disponibles próximamente.',
    },
    {
        id: 'pdf',
        emoji: '📄',
        titulo: 'PDFs',
        subtitulo: 'Guías y whitepapers',
        descripcion:
            'Descarga guías técnicas, whitepapers y documentos de referencia sobre normativas ISO, TISAX y mejores prácticas en seguridad.',
        accentColor: '#0f172a',   // casi negro
        borderColor: '#334155',
        items: [
            // { titulo: 'Guía ISO 27001', url: '#', paginas: 24 },
        ],
        placeholder: 'Los documentos PDF estarán disponibles próximamente.',
    },
    {
        id: 'cursos',
        emoji: '🎓',
        titulo: 'Cursos Gratuitos',
        subtitulo: 'Formación completa',
        descripcion:
            'Cursos estructurados y completamente gratuitos sobre ciberseguridad, IA aplicada y cumplimiento normativo, a tu propio ritmo.',
        accentColor: '#065f46',   // verde oscuro
        borderColor: '#059669',
        items: [
            { titulo: 'AI for Executives & Strategy – Coursera', url: 'https://www.coursera.org/learn/ai-for-executives--strategy', duracion: '3 semanas (10 h/sem)', nivel: 'Principiante' },
            { titulo: 'Generative AI for Executives & Business Leaders – IBM', url: 'https://www.mygreatlearning.com/academy/learn-for-free/courses/agentic-ai-and-leadership-transformation', duracion: '3 horas', nivel: 'Principiante' },
            { titulo: 'Agentic AI for Leaders – Great Learning', url: 'https://www.coursera.org/learn/generative-ai-for-executives-business-leaders-introduction', duracion: '1.5 horas', nivel: 'Principiante' },
            { titulo: 'Artificial Intelligence & Leadership 2026+ – Impodays', url: 'https://learn.mit.edu/search?free=true&q=ai', duracion: '6 horas', nivel: 'Intermedio' },
            { titulo: 'Understanding AI – Great Learning', url: 'https://www.mygreatlearning.com/academy/learn-for-free', duracion: 'Libre', nivel: 'Principiante' },
            { titulo: 'AI Revolution & Industry 5.0 – Great Learning', url: 'https://www.mygreatlearning.com/academy/learn-for-free', duracion: 'Libre', nivel: 'Principiante' },
            { titulo: 'AI Agents for Decision Making – Great Learning', url: 'https://www.mygreatlearning.com/academy/learn-for-free', duracion: 'Libre', nivel: 'Principiante' },
            { titulo: 'IBM Applied AI Essentials – Skills Network', url: 'https://www.ibm.com/new/training', duracion: 'Libre', nivel: 'Principiante' },
            { titulo: 'IBM AI Academy (Executive Series)', url: 'https://www.ibm.com/new/training', duracion: 'Libre', nivel: 'Ejecutivo' },
            { titulo: 'AI-Powered Leadership – Great Learning', url: 'https://www.mygreatlearning.com/academy/learn-for-free', duracion: 'Libre', nivel: 'Ejecutivo' },
            { titulo: 'Prompt Engineering for Executives – IBM', url: 'https://www.ibm.com/new/training', duracion: 'Libre', nivel: 'Ejecutivo' },
            { titulo: 'Prompt Engineering with Generative AI – Great Learning', url: 'https://www.mygreatlearning.com/academy/learn-for-free', duracion: 'Libre', nivel: 'Principiante' },
            { titulo: 'Practical Prompting for Business Leaders – Coursera', url: 'https://www.coursera.org', duracion: '1 hora', nivel: 'Principiante' },
            { titulo: 'GenAI Fundamentals for Leaders – IBM Academy', url: 'https://www.ibm.com/new/training', duracion: 'Libre', nivel: 'Ejecutivo' },
            { titulo: 'Generative AI Essentials – Coursera', url: 'https://www.coursera.org', duracion: 'Libre', nivel: 'Principiante' },
            { titulo: 'ChatGPT Productivity Projects – Coursera', url: 'https://www.coursera.org', duracion: 'Libre', nivel: 'Principiante' },
            { titulo: 'GenAI Leadership Toolkit – IBM', url: 'https://www.ibm.com/new/training', duracion: 'Libre', nivel: 'Ejecutivo' },
            { titulo: 'Generative AI & Prompting Basics – Great Learning', url: 'https://www.mygreatlearning.com/academy/learn-for-free', duracion: 'Libre', nivel: 'Principiante' },
            { titulo: 'AI Agents + Prompting – Great Learning', url: 'https://www.mygreatlearning.com/academy/learn-for-free', duracion: 'Libre', nivel: 'Principiante' },
            { titulo: 'Applied Generative AI for Business – IBM Skills Network', url: 'https://www.ibm.com/new/training', duracion: 'Libre', nivel: 'Ejecutivo' },
        ],
        placeholder: 'Los cursos gratuitos estarán disponibles próximamente.',
    },
];

// ──────────────────────────────────────────────────────────
// Tarjeta de cada categoría
// ──────────────────────────────────────────────────────────
const CategoriaCard = ({ cat, index, onSelect }) => {
    const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.12 });
    const [hovered, setHovered] = useState(false);

    return (
        <div
            ref={ref}
            className={`${inView ? 'animate__animated animate__fadeInUp' : 'opacity-0'}`}
            style={{ animationDelay: `${index * 0.18}s` }}
        >
            <div
                onClick={() => onSelect(cat.id)}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                    cursor: 'pointer',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: hovered
                        ? '0 20px 40px rgba(6, 28, 91, 0.22)'
                        : '0 4px 18px rgba(6, 28, 91, 0.10)',
                    transform: hovered ? 'translateY(-8px)' : 'translateY(0)',
                    transition: 'transform 0.35s ease, box-shadow 0.35s ease',
                    background: '#fff',
                    border: `2px solid ${hovered ? cat.borderColor : '#e5e7eb'}`,
                }}
            >
                {/* Barra superior de color */}
                <div
                    style={{
                        background: `linear-gradient(135deg, ${NAV_COLOR} 0%, ${cat.accentColor} 100%)`,
                        padding: '32px 24px 24px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '12px',
                        minHeight: '180px',
                        justifyContent: 'center',
                    }}
                >
                    {/* Ícono */}
                    <div
                        style={{
                            fontSize: '3rem',
                            background: 'rgba(255,255,255,0.15)',
                            borderRadius: '50%',
                            width: '80px',
                            height: '80px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'transform 0.3s ease',
                            transform: hovered ? 'scale(1.15)' : 'scale(1)',
                        }}
                    >
                        {cat.emoji}
                    </div>

                    {/* Título con fuente Escobar del sitio */}
                    <h2
                        style={{
                            fontFamily: 'Escobar',
                            fontSize: '2rem',
                            color: '#ffffff',
                            margin: 0,
                            textAlign: 'center',
                        }}
                    >
                        {cat.titulo}
                    </h2>
                </div>

                {/* Cuerpo de la tarjeta */}
                <div style={{ padding: '24px' }}>
                    <p
                        style={{
                            fontFamily: 'Montserrat',
                            fontSize: '0.9rem',
                            color: '#4b5563',
                            lineHeight: '1.65',
                            marginBottom: '20px',
                            minHeight: '60px',
                        }}
                    >
                        {cat.descripcion}
                    </p>

                    {/* Botón estilo DS cyber */}
                    <div
                        style={{
                            background: hovered ? cat.accentColor : NAV_COLOR,
                            color: '#fff',
                            fontFamily: 'Montserrat',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            padding: '12px 0',
                            borderRadius: '8px',
                            textAlign: 'center',
                            letterSpacing: '0.5px',
                            transition: 'background 0.25s ease',
                        }}
                    >
                        Ver {cat.titulo} →
                    </div>
                </div>
            </div>
        </div>
    );
};

// ──────────────────────────────────────────────────────────
// Panel de detalle de una categoría seleccionada
// ──────────────────────────────────────────────────────────
const PanelDetalle = ({ cat, onVolver }) => {
    const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
    const [filtroNivel, setFiltroNivel] = useState('Todos');

    // Obtener niveles únicos del arreglo de items
    const nivelesDisponibles = ['Todos', ...new Set((cat.items || []).map(i => i.nivel))];

    return (
        <div
            ref={ref}
            className={`${inView ? 'animate__animated animate__fadeIn' : 'opacity-0'}`}
        >
            {/* Encabezado del panel */}
            <div
                style={{
                    background: `linear-gradient(135deg, ${NAV_COLOR} 0%, ${cat.accentColor} 100%)`,
                    borderRadius: '16px',
                    padding: '32px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '20px',
                    alignItems: 'center',
                    boxShadow: '0 8px 24px rgba(6, 28, 91, 0.25)',
                }}
            >
                <div style={{ fontSize: '3.5rem' }}>{cat.emoji}</div>
                <div style={{ flex: 1, minWidth: '200px' }}>
                    <h2
                        style={{
                            fontFamily: 'Escobar',
                            fontSize: '2rem',
                            color: '#fff',
                            margin: 0,
                        }}
                    >
                        {cat.titulo}
                    </h2>
                    <p
                        style={{
                            fontFamily: 'Montserrat',
                            color: 'rgba(255,255,255,0.8)',
                            fontSize: '0.9rem',
                            margin: '6px 0 0',
                        }}
                    >
                        {cat.descripcion}
                    </p>
                </div>
                <button
                    onClick={onVolver}
                    style={{
                        background: 'rgba(255,255,255,0.18)',
                        border: '1px solid rgba(255,255,255,0.4)',
                        color: '#fff',
                        fontFamily: 'Montserrat',
                        fontWeight: 700,
                        padding: '10px 22px',
                        borderRadius: '50px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        transition: 'background 0.2s ease',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
                >
                    ← Volver
                </button>
            </div>

            {/* Contenido: items o estado vacío */}
            <div style={{ marginTop: '36px' }}>
                {cat.items && cat.items.length > 0 ? (
                    // ── Tabla de cursos ──────────────────────────
                    <div>
                        {/* ── Barra de filtros ── */}
                        <div className="rcs-filtros-container">
                            <p className="rcs-filtros-titulo">Filtrar por nivel:</p>
                            <div className="rcs-filtros">
                                {nivelesDisponibles.map(nivel => {
                                    const activo = filtroNivel === nivel;
                                    return (
                                        <button
                                            key={nivel}
                                            onClick={() => setFiltroNivel(nivel)}
                                            style={{
                                                padding: '7px 18px',
                                                borderRadius: '50px',
                                                border: `2px solid ${activo ? cat.accentColor : '#e5e7eb'}`,
                                                background: activo ? cat.accentColor : '#fff',
                                                color: activo ? '#fff' : '#4b5563',
                                                fontFamily: 'Montserrat',
                                                fontWeight: 700,
                                                fontSize: '0.8rem',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                boxShadow: activo ? `0 4px 12px ${cat.accentColor}55` : 'none',
                                            }}
                                        >
                                            {nivel}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        {/* Cabecera de la tabla (solo escritorio) */}
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 100px 110px 120px',
                                gap: '12px',
                                padding: '12px 20px',
                                background: NAV_COLOR,
                                borderRadius: '10px 10px 0 0',
                                fontFamily: 'Montserrat',
                                fontWeight: 700,
                                fontSize: '0.78rem',
                                color: 'rgba(255,255,255,0.85)',
                                letterSpacing: '0.8px',
                                textTransform: 'uppercase',
                            }}
                            className="rcs-tabla-header"
                        >
                            <div>Curso</div>
                            <div style={{ textAlign: 'center' }}>Duración</div>
                            <div style={{ textAlign: 'center' }}>Nivel</div>
                            <div style={{ textAlign: 'center' }}>Acceso</div>
                        </div>

                        {/* Filas de cursos */}
                        <div
                            style={{
                                border: '1px solid #e5e7eb',
                                borderTop: 'none',
                                borderRadius: '0 0 10px 10px',
                                overflow: 'hidden',
                            }}
                        >
                            {(() => {
                                const itemsFiltrados = filtroNivel === 'Todos'
                                    ? cat.items
                                    : cat.items.filter(i => i.nivel === filtroNivel);

                                if (itemsFiltrados.length === 0) {
                                    return (
                                        <div style={{
                                            padding: '40px',
                                            textAlign: 'center',
                                            background: '#f8fafc',
                                        }}>
                                            <p style={{
                                                fontFamily: 'Montserrat',
                                                color: '#9ca3af',
                                                fontSize: '0.9rem',
                                                margin: 0,
                                            }}>
                                                No hay cursos para el nivel “{filtroNivel}”.
                                            </p>
                                        </div>
                                    );
                                }

                                return itemsFiltrados.map((item, idx) => {
                                    const nivelColor =
                                        item.nivel === 'Principiante' ? { bg: '#dcfce7', text: '#166534' }
                                            : item.nivel === 'Intermedio' ? { bg: '#fef9c3', text: '#854d0e' }
                                                : item.nivel === 'Ejecutivo' ? { bg: '#dbeafe', text: '#1e40af' }
                                                    : { bg: '#ede9fe', text: '#5b21b6' };

                                    return (
                                        <div
                                            key={idx}
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr',
                                                gap: '10px',
                                                padding: '18px 20px',
                                                background: idx % 2 === 0 ? '#ffffff' : '#f8fafc',
                                                borderBottom: idx < itemsFiltrados.length - 1 ? '1px solid #e5e7eb' : 'none',
                                                transition: 'background 0.2s ease',
                                            }}
                                            className="curso-fila"
                                            onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                                            onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? '#ffffff' : '#f8fafc'}
                                        >
                                            {/* Vista móvil: tarjeta mejorada */}
                                            <div className="rcs-curso-fila-mobile">
                                                <p style={{ fontFamily: 'Escobar', fontSize: '1.2rem', color: NAV_COLOR, margin: 0, lineHeight: 1.3 }}>
                                                    {item.titulo}
                                                </p>

                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    marginTop: '4px',
                                                    borderBottom: '1px solid #f3f4f6',
                                                    paddingBottom: '14px',
                                                    marginBottom: '4px'
                                                }}>
                                                    <span style={{ fontFamily: 'Montserrat', fontSize: '0.85rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <span style={{ fontSize: '1rem' }}>⏱</span> {item.duracion}
                                                    </span>
                                                    <div style={{
                                                        display: 'inline-block',
                                                        fontFamily: 'Montserrat', fontWeight: 700, fontSize: '0.75rem',
                                                        padding: '4px 12px', borderRadius: '50px',
                                                        background: nivelColor.bg,
                                                        color: nivelColor.text,
                                                        WebkitTextFillColor: nivelColor.text,
                                                        WebkitTextStroke: 0,
                                                        lineHeight: '1.6',
                                                    }}>
                                                        {item.nivel}
                                                    </div>
                                                </div>

                                                <a
                                                    href={item.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        display: 'inline-block',
                                                        background: cat.accentColor,
                                                        color: '#fff',
                                                        fontFamily: 'Montserrat',
                                                        fontWeight: 700,
                                                        fontSize: '0.85rem',
                                                        padding: '12px 18px',
                                                        borderRadius: '8px',
                                                        textDecoration: 'none',
                                                        textAlign: 'center',
                                                        width: '100%',
                                                        transition: 'opacity 0.2s ease',
                                                    }}
                                                    onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                                                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                                >
                                                    Acceder al curso →
                                                </a>
                                            </div>

                                            {/* Vista escritorio: grid de 4 columnas */}
                                            <div
                                                className="rcs-curso-fila-desktop"
                                                style={{
                                                    gridTemplateColumns: '1fr 100px 110px 120px',
                                                    gap: '12px',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <p style={{ fontFamily: 'Escobar', fontSize: '1.1rem', color: NAV_COLOR, margin: 0 }}>
                                                    {item.titulo}
                                                </p>
                                                <p style={{ fontFamily: 'Montserrat', fontSize: '0.85rem', color: '#6b7280', margin: 0, textAlign: 'center' }}>
                                                    ⏱ {item.duracion}
                                                </p>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{
                                                        display: 'inline-block',
                                                        fontFamily: 'Montserrat',
                                                        fontWeight: 700,
                                                        fontSize: '0.78rem',
                                                        padding: '5px 14px',
                                                        borderRadius: '50px',
                                                        background: nivelColor.bg,
                                                        color: nivelColor.text,
                                                        WebkitTextFillColor: nivelColor.text,
                                                        WebkitTextStroke: 0,
                                                        lineHeight: '1.5',
                                                        letterSpacing: '0.3px',
                                                    }}>
                                                        {item.nivel}
                                                    </div>
                                                </div>
                                                <div style={{ textAlign: 'center' }}>
                                                    <a
                                                        href={item.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            display: 'inline-block',
                                                            background: cat.accentColor,
                                                            color: '#fff',
                                                            fontFamily: 'Montserrat',
                                                            fontWeight: 700,
                                                            fontSize: '0.8rem',
                                                            padding: '8px 18px',
                                                            borderRadius: '6px',
                                                            textDecoration: 'none',
                                                            transition: 'opacity 0.2s ease',
                                                        }}
                                                        onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                                                        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                                    >
                                                        Acceder →
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    </div>
                ) : (
                    /* Estado "Próximamente" */
                    <div
                        style={{
                            textAlign: 'center',
                            padding: '60px 20px',
                            background: '#fff',
                            borderRadius: '16px',
                            border: '2px dashed #d1d5db',
                        }}
                    >
                        <div
                            style={{ fontSize: '4rem', opacity: 0.25, marginBottom: '20px' }}
                        >
                            {cat.emoji}
                        </div>
                        <h3
                            style={{
                                fontFamily: 'Escobar',
                                fontSize: '1.5rem',
                                color: NAV_COLOR,
                                marginBottom: '10px',
                            }}
                        >
                            Contenido en preparación
                        </h3>
                        <p
                            style={{
                                fontFamily: 'Montserrat',
                                color: '#6b7280',
                                fontSize: '0.9rem',
                                maxWidth: '400px',
                                margin: '0 auto 24px',
                            }}
                        >
                            {cat.placeholder}
                        </p>
                        <div
                            style={{
                                display: 'inline-block',
                                background: '#f3f4f6',
                                color: '#6b7280',
                                fontFamily: 'Montserrat',
                                fontWeight: 700,
                                fontSize: '0.77rem',
                                padding: '8px 20px',
                                borderRadius: '50px',
                                letterSpacing: '0.5px',
                            }}
                        >
                            🚀 PRÓXIMAMENTE
                        </div>
                    </div>
                )}
            </div>
        </div >
    );
};

// ──────────────────────────────────────────────────────────
// Formulario de bienvenida — aparece ANTES de ver los recursos
// ──────────────────────────────────────────────────────────
const FormBienvenidaRecursos = ({ onAcceder }) => {
    const [form, setForm] = useState({ nombre: '', apellido: '', correo: '' });
    const [errors, setErrors] = useState({});
    const [enviando, setEnviando] = useState(false);
    const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

    const validate = () => {
        const e = {};
        if (!form.nombre.trim()) e.nombre = 'El nombre es requerido.';
        if (!form.apellido.trim()) e.apellido = 'El apellido es requerido.';
        if (!form.correo.trim()) e.correo = 'El correo es requerido.';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.correo))
            e.correo = 'Ingresa un correo válido.';
        return e;
    };

    const handleChange = (e) =>
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        const e2 = validate();
        if (Object.keys(e2).length) { setErrors(e2); return; }
        setEnviando(true);

        let esNuevoUsuario = true;
        try {
            const { error: insertError } = await supabase
                .from('recursos_leads')
                .insert([
                    {
                        nombre: form.nombre.trim(),
                        apellido: form.apellido.trim(),
                        correo: form.correo.trim()
                    }
                ]);

            if (insertError) {
                // Si el error es '23505', significa que el correo ya está duplicado (PostgreSQL Unique Violation)
                if (insertError.code === '23505') {
                    console.log("El correo ya estaba registrado. Actualizando contador...");
                    esNuevoUsuario = false;

                    try {
                        // Rescatamos el dato actual de veces_ingreso y lo sumamos
                        const { data: usuarioExistente } = await supabase
                            .from('recursos_leads')
                            .select('veces_ingreso')
                            .eq('correo', form.correo.trim())
                            .single();

                        if (usuarioExistente) {
                            const cantidadActual = usuarioExistente.veces_ingreso || 1;
                            await supabase
                                .from('recursos_leads')
                                .update({ veces_ingreso: cantidadActual + 1 })
                                .eq('correo', form.correo.trim());
                        }
                    } catch (updateErr) {
                        console.error("Error al intentar actualizar el contador:", updateErr);
                    }
                } else {
                    console.error("Error guardando en Supabase:", insertError.message);
                }
            }
        } catch (err) {
            console.error("Error de red con Supabase:", err);
        } finally {
            // Revelar los recursos enviando los datos y si es nuevo usuario
            onAcceder({ ...form, esNuevo: esNuevoUsuario });
        }
    };

    const inputStyle = (hasError) => ({
        width: '100%',
        padding: '12px 16px',
        border: `2px solid ${hasError ? '#ef4444' : '#d1d5db'}`,
        borderRadius: '8px',
        fontFamily: 'Montserrat',
        fontSize: '0.9rem',
        color: '#111827',
        outline: 'none',
        transition: 'border-color 0.2s ease',
        background: '#fff',
        boxSizing: 'border-box',
    });

    return (
        <div
            ref={ref}
            className={`${inView ? 'animate__animated animate__fadeIn' : 'opacity-0'}`}
            style={{ marginBottom: '16px' }}
        >
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '48px',
                    alignItems: 'center',
                    background: '#fff',
                    borderRadius: '20px',
                    padding: '48px',
                    boxShadow: '0 8px 32px rgba(6,28,91,0.10)',
                    border: '1px solid #e5e7eb',
                }}
                className="rcs-welcome-grid"
            >
                {/* ── Lado izquierdo: texto de bienvenida ── */}
                <div className="rcs-hide-mobile">
                    {/* Ícono decorativo */}
                    <div style={{
                        width: '64px', height: '64px',
                        background: `linear-gradient(135deg, ${NAV_COLOR}, #3b82f6)`,
                        borderRadius: '16px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '2rem',
                        marginBottom: '24px',
                        boxShadow: '0 4px 16px rgba(6,28,91,0.2)',
                    }}>
                        📚
                    </div>

                    <h2 style={{
                        fontFamily: 'Escobar',
                        fontSize: 'clamp(1.6rem, 2.5vw, 2.2rem)',
                        color: NAV_COLOR,
                        margin: '0 0 16px',
                        lineHeight: 1.3,
                    }}>
                        Bienvenido a los recursos gratuitos de DS cyber
                    </h2>

                    <p style={{
                        fontFamily: 'Montserrat',
                        fontSize: '0.95rem',
                        color: '#4b5563',
                        lineHeight: 1.75,
                        margin: '0 0 20px',
                    }}>
                        DS cyber ha recopilado recursos especializados de las mejores plataformas
                        para que tú puedas acceder de forma gratuita a contenido de calidad
                        en ciberseguridad, inteligencia artificial y consultoría tecnológica.
                    </p>

                    <p style={{
                        fontFamily: 'Montserrat',
                        fontSize: '0.88rem',
                        color: '#6b7280',
                        lineHeight: 1.65,
                    }}>
                        Completa el formulario para acceder a nuestra biblioteca digital,
                        incluyendo videos, PDFs y cursos seleccionados por nuestros expertos. ¡Es completamente gratuito!
                    </p>

                    {/* Beneficios */}
                    <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {[
                            '✅ Acceso inmediato y gratuito',
                            '✅ Cursos de Coursera, IBM y Great Learning',
                            '✅ Niveles: Principiante, Intermedio y Ejecutivo',
                        ].map(b => (
                            <p key={b} style={{
                                fontFamily: 'Montserrat', fontWeight: 600,
                                fontSize: '0.85rem', color: NAV_COLOR, margin: 0,
                            }}>{b}</p>
                        ))}
                    </div>
                </div>

                {/* ── Lado derecho: formulario ── */}
                <div>
                    <div style={{
                        background: '#f8fafc',
                        border: '1px solid #e5e7eb',
                        borderRadius: '16px',
                        padding: '32px',
                    }}>
                        <h3 style={{
                            fontFamily: 'Escobar',
                            fontSize: '1.4rem',
                            color: NAV_COLOR,
                            margin: '0 0 6px',
                        }}>
                            Regístrate para acceder a los recursos
                        </h3>
                        <p style={{
                            fontFamily: 'Montserrat', fontSize: '0.85rem',
                            color: '#6b7280', margin: '0 0 26px',
                            lineHeight: 1.5
                        }}>
                            Activa tu acceso gratuito e inmediato completando estos datos básicos.
                        </p>

                        <form onSubmit={handleSubmit} noValidate>
                            {/* Nombre */}
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{
                                    display: 'block', fontFamily: 'Montserrat',
                                    fontWeight: 700, fontSize: '0.78rem',
                                    color: NAV_COLOR, marginBottom: '6px',
                                    letterSpacing: '0.5px', textTransform: 'uppercase',
                                }}>
                                    Nombre
                                </label>
                                <input
                                    type="text"
                                    name="nombre"
                                    placeholder="Tu nombre"
                                    value={form.nombre}
                                    onChange={handleChange}
                                    style={inputStyle(!!errors.nombre)}
                                    onFocus={e => e.target.style.borderColor = NAV_COLOR}
                                    onBlur={e => e.target.style.borderColor = errors.nombre ? '#ef4444' : '#d1d5db'}
                                />
                                {errors.nombre && (
                                    <p style={{ fontFamily: 'Montserrat', fontSize: '0.75rem', color: '#ef4444', margin: '4px 0 0' }}>
                                        {errors.nombre}
                                    </p>
                                )}
                            </div>

                            {/* Apellido */}
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{
                                    display: 'block', fontFamily: 'Montserrat',
                                    fontWeight: 700, fontSize: '0.78rem',
                                    color: NAV_COLOR, marginBottom: '6px',
                                    letterSpacing: '0.5px', textTransform: 'uppercase',
                                }}>
                                    Apellido
                                </label>
                                <input
                                    type="text"
                                    name="apellido"
                                    placeholder="Tu apellido"
                                    value={form.apellido}
                                    onChange={handleChange}
                                    style={inputStyle(!!errors.apellido)}
                                    onFocus={e => e.target.style.borderColor = NAV_COLOR}
                                    onBlur={e => e.target.style.borderColor = errors.apellido ? '#ef4444' : '#d1d5db'}
                                />
                                {errors.apellido && (
                                    <p style={{ fontFamily: 'Montserrat', fontSize: '0.75rem', color: '#ef4444', margin: '4px 0 0' }}>
                                        {errors.apellido}
                                    </p>
                                )}
                            </div>

                            {/* Correo */}
                            <div style={{ marginBottom: '28px' }}>
                                <label style={{
                                    display: 'block', fontFamily: 'Montserrat',
                                    fontWeight: 700, fontSize: '0.78rem',
                                    color: NAV_COLOR, marginBottom: '6px',
                                    letterSpacing: '0.5px', textTransform: 'uppercase',
                                }}>
                                    Correo electrónico
                                </label>
                                <input
                                    type="email"
                                    name="correo"
                                    placeholder="tu@correo.com"
                                    value={form.correo}
                                    onChange={handleChange}
                                    style={inputStyle(!!errors.correo)}
                                    onFocus={e => e.target.style.borderColor = NAV_COLOR}
                                    onBlur={e => e.target.style.borderColor = errors.correo ? '#ef4444' : '#d1d5db'}
                                />
                                {errors.correo && (
                                    <p style={{ fontFamily: 'Montserrat', fontSize: '0.75rem', color: '#ef4444', margin: '4px 0 0' }}>
                                        {errors.correo}
                                    </p>
                                )}
                            </div>

                            {/* Botón submit */}
                            <button
                                type="submit"
                                disabled={enviando}
                                style={{
                                    width: '100%',
                                    padding: '14px',
                                    background: enviando ? '#9ca3af' : `linear-gradient(135deg, ${NAV_COLOR}, #3b82f6)`,
                                    color: '#fff',
                                    fontFamily: 'Montserrat',
                                    fontWeight: 700,
                                    fontSize: '0.95rem',
                                    border: 'none',
                                    borderRadius: '10px',
                                    cursor: enviando ? 'not-allowed' : 'pointer',
                                    letterSpacing: '0.5px',
                                    transition: 'opacity 0.2s ease, transform 0.2s ease',
                                    boxSizing: 'border-box',
                                }}
                                onMouseEnter={e => { if (!enviando) e.currentTarget.style.opacity = '0.9'; }}
                                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                            >
                                {enviando ? '⏳ Cargando...' : 'Acceder a los Recursos →'}
                            </button>

                            <p style={{
                                fontFamily: 'Montserrat', fontSize: '0.72rem',
                                color: '#9ca3af', textAlign: 'center',
                                margin: '12px 0 0',
                            }}>
                                🔒 Tu información es segura y nunca será compartida.
                            </p>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ──────────────────────────────────────────────────────────
// Página principal /recursos
// ──────────────────────────────────────────────────────────
const RecursosPage = () => {
    const [categoriaActiva, setCategoriaActiva] = useState(null);
    const [recursosAcceso, setRecursosAcceso] = useState(null); // guardará { nombre, esNuevo } cuando el formulario sea enviado
    const [refHero, inViewHero] = useInView({ triggerOnce: true, threshold: 0.1 });

    const catActiva = CATEGORIAS.find((c) => c.id === categoriaActiva);

    const handleSelect = (id) => {
        setCategoriaActiva(id);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleVolver = () => {
        setCategoriaActiva(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <>
            <style>{RECURSOS_STYLES}</style>
            <div style={{ minHeight: '100vh', background: '#f8fafc' }}>

                {/* ── Hero Banner ──────────────────────────────── */}
                <div
                    style={{
                        background: `linear-gradient(135deg, #061C5B 0%, #0A2B7C 60%, #0d3a9e 100%)`,
                        padding: '72px 24px 80px',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    {/* Decoración geométrica */}
                    <div
                        style={{
                            position: 'absolute', top: '-60px', right: '-60px',
                            width: '300px', height: '300px',
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.04)',
                            pointerEvents: 'none',
                        }}
                    />
                    <div
                        style={{
                            position: 'absolute', bottom: '-40px', left: '-40px',
                            width: '200px', height: '200px',
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.04)',
                            pointerEvents: 'none',
                        }}
                    />

                    <div
                        ref={refHero}
                        className={`${inViewHero ? 'animate__animated animate__fadeInDown' : 'opacity-0'}`}
                        style={{ maxWidth: '720px', margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}
                    >


                        {/* Badge */}
                        <div
                            style={{
                                display: 'inline-block',
                                background: 'rgba(255,255,255,0.12)',
                                border: '1px solid rgba(255,255,255,0.25)',
                                color: '#93c5fd',
                                fontFamily: 'Montserrat',
                                fontWeight: 700,
                                fontSize: '0.72rem',
                                letterSpacing: '2px',
                                padding: '6px 20px',
                                borderRadius: '50px',
                                marginBottom: '22px',
                                textTransform: 'uppercase',
                            }}
                        >
                            Biblioteca Digital Gratuita
                        </div>

                        {/* Título principal — usa Nikaia como h1 del sitio */}
                        <h1
                            style={{
                                fontFamily: 'Nikaia',
                                fontSize: 'clamp(2.4rem, 5vw, 3.8rem)',
                                color: '#ffffff',
                                margin: '0 0 20px',
                                lineHeight: 1.15,
                                letterSpacing: '1px',
                            }}
                        >
                            Recursos DS cyber
                        </h1>

                        {/* Subtítulo */}
                        <p
                            style={{
                                fontFamily: 'Montserrat',
                                fontSize: '1.05rem',
                                color: 'rgba(255,255,255,0.78)',
                                lineHeight: 1.75,
                                maxWidth: '560px',
                                margin: '0 auto',
                            }}
                        >
                            Accede gratuitamente a nuestra colección de <strong style={{ color: '#fff' }}>videos</strong>,{' '}
                            <strong style={{ color: '#fff' }}>PDFs</strong> y{' '}
                            <strong style={{ color: '#fff' }}>cursos</strong> creados por nuestros expertos
                            en ciberseguridad, inteligencia artificial y consultoría tecnológica.
                        </p>

                        {/* Línea decorativa */}
                        <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'center', gap: '6px' }}>
                            <div style={{ height: '3px', width: '48px', background: '#3b82f6', borderRadius: '2px' }} />
                            <div style={{ height: '3px', width: '16px', background: '#93c5fd', borderRadius: '2px' }} />
                            <div style={{ height: '3px', width: '8px', background: 'rgba(255,255,255,0.3)', borderRadius: '2px' }} />
                        </div>
                    </div>
                </div>

                {/* ── Contenido principal ──────────────────────── */}
                <div className="rcs-main-content" style={{ maxWidth: '1100px', margin: '0 auto', padding: '56px 24px 80px' }}>

                    {!recursosAcceso ? (
                        /* Formulario de bienvenida ANTES de ver cualquier recurso */
                        <FormBienvenidaRecursos onAcceder={(data) => { setRecursosAcceso(data); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
                    ) : !categoriaActiva ? (
                        <>
                            {/* Título de sección */}
                            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
                                <h2
                                    style={{
                                        fontFamily: 'Escobar',
                                        fontSize: 'clamp(1.8rem, 3vw, 2.4rem)',
                                        color: NAV_COLOR,
                                        margin: '0 0 12px',
                                    }}
                                >
                                    {recursosAcceso.esNuevo
                                        ? `¡Bienvenido, ${recursosAcceso.nombre}!`
                                        : `¡Qué gusto verte de nuevo, ${recursosAcceso.nombre}!`}
                                </h2>
                                <p
                                    style={{
                                        fontFamily: 'Montserrat',
                                        color: '#6b7280',
                                        fontSize: '1.05rem',
                                    }}
                                >
                                    {recursosAcceso.esNuevo
                                        ? 'Nos alegra que te unas a nuestra biblioteca. Selecciona el tipo de recurso que deseas explorar.'
                                        : 'Gracias por seguir aprendiendo con nosotros. Selecciona el tipo de recurso que deseas explorar.'}
                                </p>
                            </div>

                            {/* Grid de tarjetas */}
                            <div className="rcs-categorias-grid">
                                {CATEGORIAS.map((cat, idx) => (
                                    <CategoriaCard key={cat.id} cat={cat} index={idx} onSelect={handleSelect} />
                                ))}
                            </div>
                        </>
                    ) : (
                        <PanelDetalle cat={catActiva} onVolver={handleVolver} />
                    )}
                </div>
            </div>
        </>
    );
};

export default RecursosPage;
